import React, { useEffect, useMemo, useRef, useState } from "react";

const HEADLINE_WORD = "Relentless";

const tickerItems = [
  "200+ startups screened/month",
  "10+ portfolio companies",
  "50+ operator mentors",
  "1 acquisition",
  "₹100 Cr fund",
  "6-month PMF validation",
  "52.6% startups from Tier 2 & 3",
  "₹2–4 Cr per deal",
];

export default function Hero({ onOpenPitch, onOpenInvest }) {
  const [mounted, setMounted] = useState(false);
  const [sparks, setSparks] = useState([]);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  const heroRef = useRef(null);
  const orbRef = useRef(null);
  const headlineRef = useRef(null);
  const ctaRef = useRef(null);
  const cursorRef = useRef(null);
  const sparkIdRef = useRef(0);
  const lastSparkAt = useRef(0);

  useEffect(() => setMounted(true), []);

  const shapes = useMemo(() => {
    if (!mounted) return [];
    const colors = ["#355E3B", "#f4b942"];
    return Array.from({ length: 32 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * 100,
      size: Math.random() * 56 + 18,
      delay: Math.random() * 4,
      duration: Math.random() * 6 + 6,
      opacity: Math.random() * 0.25 + 0.12,
      type: i % 4,
      rotation: Math.floor(Math.random() * 360),
      color: colors[i % colors.length],
    }));
  }, [mounted]);

  useEffect(() => {
    if (!mounted) return;
    const handleMove = (e) => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const nx = e.clientX / w - 0.5;
      const ny = e.clientY / h - 0.5;
      setMouse({ x: nx, y: ny });
      if (orbRef.current) orbRef.current.style.transform = `translate3d(${nx * 36}px, ${ny * 36}px, 0)`;
      if (headlineRef.current) headlineRef.current.style.transform = `perspective(1200px) rotateX(${ny * -3}deg) rotateY(${nx * 3}deg) translateZ(0)`;
      if (cursorRef.current) cursorRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;
      if (ctaRef.current) {
        const rect = ctaRef.current.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const dist = Math.hypot(dx, dy);
        const radius = 160;
        if (dist < radius && dist > 0) {
          const pull = (1 - dist / radius) * 18;
          ctaRef.current.style.transform = `translate3d(${(dx / dist) * pull}px, ${(dy / dist) * pull}px, 0)`;
        } else ctaRef.current.style.transform = "";
      }
      const now = performance.now();
      if (now - lastSparkAt.current > 60) {
        lastSparkAt.current = now;
        const id = sparkIdRef.current++;
        const colors = ["#355E3B", "#f4b942"];
        setSparks((prev) => [...prev.slice(-14), { id, x: e.clientX, y: e.clientY, size: 6 + Math.random() * 8, color: colors[Math.floor(Math.random() * colors.length)] }]);
        setTimeout(() => setSparks((prev) => prev.filter((s) => s.id !== id)), 900);
      }
    };
    window.addEventListener("mousemove", handleMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMove);
  }, [mounted]);

  return (
    <div id="home" ref={heroRef} data-testid="hero-section" className="relative w-full overflow-hidden bg-white" style={{ minHeight: "100svh" }}>
      <div className="absolute inset-0 opacity-[0.12] pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: "linear-gradient(rgba(53,94,59,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(53,94,59,0.6) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          animation: "ss-grid-flow 22s linear infinite",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 30%, transparent 78%)",
        }} />
      </div>

      <div className="absolute inset-0 pointer-events-none">
        {shapes.map((shape) => {
          const baseStyle = { left: `${shape.left}%`, top: `${shape.top}%`, width: shape.size, height: shape.size, opacity: shape.opacity, animation: `ss-shape-float ${shape.duration}s ease-in-out ${shape.delay}s infinite`, transform: `translate(${mouse.x * 30}px, ${mouse.y * 30}px) rotate(${shape.rotation}deg)`, transition: "transform 0.6s cubic-bezier(0.22, 1, 0.36, 1)" };
          if (shape.type === 0) return <div key={shape.id} className="absolute rounded-full" style={{ ...baseStyle, background: `radial-gradient(circle, ${shape.color} 0%, transparent 70%)`, filter: "blur(2px)" }} />;
          if (shape.type === 1) return <div key={shape.id} className="absolute" style={{ ...baseStyle, width: 0, height: 0, borderLeft: `${shape.size / 2}px solid transparent`, borderRight: `${shape.size / 2}px solid transparent`, borderBottom: `${shape.size}px solid ${shape.color}`, filter: "blur(2px)" }} />;
          if (shape.type === 2) return <div key={shape.id} className="absolute rounded-md" style={{ ...baseStyle, background: `linear-gradient(135deg, ${shape.color}, transparent)`, filter: "blur(2px)" }} />;
          return <div key={shape.id} className="absolute rounded-full border-2" style={{ ...baseStyle, borderColor: shape.color, background: "transparent", filter: "blur(0.5px)" }} />;
        })}
      </div>

      <div ref={orbRef} className="absolute inset-0 transition-transform duration-700 ease-out will-change-transform pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[640px] h-[640px] rounded-full mix-blend-multiply opacity-50" style={{ background: "radial-gradient(closest-side, rgba(53,94,59,0.45), transparent 70%)", filter: "blur(50px)", animation: "ss-blob 18s ease-in-out infinite" }} />
        <div className="absolute -bottom-40 -right-32 w-[720px] h-[720px] rounded-full mix-blend-multiply opacity-50" style={{ background: "radial-gradient(closest-side, rgba(244,185,66,0.45), transparent 70%)", filter: "blur(60px)", animation: "ss-blob 22s ease-in-out infinite reverse" }} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full mix-blend-multiply opacity-40" style={{ background: "radial-gradient(closest-side, rgba(53,94,59,0.32), transparent 70%)", filter: "blur(70px)", animation: "ss-blob 26s ease-in-out infinite", animationDelay: "-6s" }} />
      </div>

      {mounted && (
        <div ref={cursorRef} className="hidden md:block fixed top-0 left-0 w-3 h-3 rounded-full bg-[#f4b942] mix-blend-multiply pointer-events-none z-[80]" style={{ transition: "width 0.2s, height 0.2s, opacity 0.3s" }} />
      )}

      <div className="hidden md:block fixed inset-0 pointer-events-none z-[70]">
        {sparks.map((s) => (
          <span key={s.id} className="absolute rounded-full" style={{ left: s.x, top: s.y, width: s.size, height: s.size, background: s.color, filter: "blur(2px)", transform: "translate(-50%, -50%)", animation: "ss-spark 0.9s cubic-bezier(0.22, 1, 0.36, 1) forwards" }} />
        ))}
      </div>

      <div className="relative z-10 flex flex-col items-center justify-center px-6 lg:px-10 text-center" style={{ minHeight: "100svh", paddingTop: "clamp(6rem, 14vh, 9rem)", paddingBottom: "clamp(6rem, 14vh, 9rem)", gap: "clamp(1.5rem, 3vh, 3rem)" }}>
        {/* Looping germinating sapling */}
        <svg
          width="140"
          height="175"
          viewBox="0 0 80 100"
          fill="none"
          className="-mb-2"
          aria-hidden="true"
          style={{ animation: mounted ? "ss-fade-up 0.8s 0.0s both" : "none" }}
        >
          {/* Soil line */}
          <line
            x1="14" y1="92" x2="66" y2="92"
            stroke="#124725"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeDasharray="3 4"
            opacity="0.55"
          />

          {/* Seed pulse halo (below ground) */}
          <circle
            className="ss-grow-seed-pulse"
            cx="40" cy="96" r="4"
            fill="none"
            stroke="#8B5A2B"
            strokeWidth="1.2"
          />
          {/* Brown seed - below ground */}
          <ellipse
            className="ss-grow-seed"
            cx="40" cy="96" rx="3.6" ry="4.4"
            fill="#6B4226"
            stroke="#3D2615"
            strokeWidth="0.5"
            style={{ filter: "drop-shadow(0 0 3px rgba(107,66,38,0.45))" }}
          />
          {/* Tiny root tendril */}
          <path
            className="ss-grow-seed"
            d="M 40 100 Q 38 102, 36 103 M 40 100 Q 42 102, 44 103"
            stroke="#3D2615"
            strokeWidth="0.7"
            strokeLinecap="round"
            fill="none"
            opacity="0.7"
          />

          {/* Stem (grows from seed upward through soil) */}
          <line
            className="ss-grow-stem"
            x1="40" y1="92" x2="40" y2="32"
            stroke="#124725"
            strokeWidth="2"
            strokeLinecap="round"
          />

          {/* Left leaf */}
          <path
            className="ss-grow-leaf-l"
            d="M 40 52 Q 22 46, 16 30 Q 30 32, 40 52 Z"
            fill="#AECE4F"
            stroke="#124725"
            strokeWidth="1"
            strokeLinejoin="round"
          />

          {/* Right leaf */}
          <path
            className="ss-grow-leaf-r"
            d="M 40 42 Q 58 36, 64 20 Q 50 22, 40 42 Z"
            fill="#AECE4F"
            stroke="#124725"
            strokeWidth="1"
            strokeLinejoin="round"
          />

          {/* Crown spark halo */}
          <circle
            className="ss-grow-crown-halo"
            cx="40" cy="16" r="6"
            fill="none"
            stroke="#E8A317"
            strokeWidth="1.2"
          />
          {/* Crown spark (amber circle on top with rays) */}
          <g className="ss-grow-crown">
            <line x1="40" y1="2" x2="40" y2="8" stroke="#E8A317" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="40" y1="24" x2="40" y2="30" stroke="#E8A317" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="26" y1="16" x2="32" y2="16" stroke="#E8A317" strokeWidth="1.5" strokeLinecap="round" />
            <line x1="48" y1="16" x2="54" y2="16" stroke="#E8A317" strokeWidth="1.5" strokeLinecap="round" />
            <circle
              cx="40" cy="16" r="3.4"
              fill="#E8A317"
              style={{ filter: "drop-shadow(0 0 5px rgba(232,163,23,0.9))" }}
            />
          </g>
        </svg>

        <div className="relative inline-block">
          <h1 ref={headlineRef} className="font-serif-display max-w-6xl font-semibold text-[#0E3320] text-balance transition-transform duration-300 ease-out will-change-transform" style={{ animation: mounted ? "ss-fade-up 1s 0.15s both" : "none", fontSize: "clamp(3rem, 7vw, 6.5rem)", lineHeight: 1.04, letterSpacing: "-0.025em" }}>
            Backing India&rsquo;s Most{" "}
            <span className="relative inline-block align-baseline">
              <span
                style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontStyle: "italic",
                  fontWeight: 800,
                  color: "#B8862B",
                  letterSpacing: "-0.02em",
                  textShadow: "0 1px 0 rgba(184,134,43,0.08)",
                }}
              >
                {HEADLINE_WORD}
              </span>
              <svg className="absolute -bottom-3 left-0 w-full" height="12" viewBox="0 0 300 12" preserveAspectRatio="none" fill="none">
                <path
                  d="M3 7 Q75 1, 150 5 T297 5"
                  stroke="#B8862B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  style={{
                    strokeDasharray: 320,
                    strokeDashoffset: mounted ? 0 : 320,
                    transition: "stroke-dashoffset 1.4s 0.7s ease-out",
                    opacity: 0.85,
                  }}
                />
              </svg>
              {/* tiny serif flourish dot */}
              <span
                className="absolute -top-1 -right-2 w-1.5 h-1.5 rounded-full"
                style={{
                  background: "#E8A317",
                  boxShadow: "0 0 6px rgba(232,163,23,0.7)",
                  opacity: mounted ? 1 : 0,
                  transition: "opacity 0.6s 1.4s ease",
                }}
              />
            </span>{" "}
            Founders.
          </h1>
          <h1 aria-hidden className="font-serif-display absolute top-0 left-0 w-full font-semibold text-balance opacity-0 pointer-events-none" style={{ fontSize: "clamp(2.5rem, 6vw, 5.5rem)", lineHeight: 1.05, letterSpacing: "-0.025em" }}>
            Backing India&rsquo;s Most {HEADLINE_WORD} Founders.
          </h1>
        </div>

        <p className="max-w-3xl text-balance font-sans-jakarta text-[#355E3B]/75" style={{ animation: mounted ? "ss-fade-up 1s 0.35s both" : "none", fontSize: "clamp(1rem, 1.4vw, 1.25rem)", lineHeight: 1.55 }}>
          From <span className="text-[#355E3B] font-semibold">First Spark</span> to <span className="text-[#f4b942] font-semibold">Final Scale</span> — a venture studio built for <span className="text-[#355E3B] font-semibold">builders</span>, not boardrooms. Hands-on capital for Tier 2 & 3 founders who refuse to wait their turn.
        </p>

        <div className="flex flex-col sm:flex-row items-center gap-4" style={{ animation: mounted ? "ss-fade-up 1s 0.55s both" : "none" }}>
          <button ref={ctaRef} type="button" data-testid="hero-founder-cta" onClick={onOpenPitch} className="group relative inline-flex items-center justify-center gap-3 px-9 py-4 bg-[#355E3B] text-white text-[15px] font-semibold rounded-full overflow-hidden transition-transform duration-200 ease-out will-change-transform" style={{ boxShadow: "0 18px 40px -12px rgba(53,94,59,0.5)" }}>
            <span className="absolute inset-0 bg-gradient-to-r from-[#355E3B] via-[#355E3B] to-[#355E3B] bg-[length:200%_100%] group-hover:bg-[position:100%_0] transition-[background-position] duration-700" />
            <span className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ boxShadow: "0 0 0 2px rgba(244,185,66,0.6)" }} />
            <span className="relative z-10">I&apos;m a Founder</span>
            <svg className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
          <button type="button" data-testid="hero-investor-cta" onClick={onOpenInvest} className="group inline-flex items-center gap-2 px-7 py-4 text-[15px] font-semibold text-[#355E3B] rounded-full border border-[#355E3B]/25 hover:border-[#355E3B] hover:bg-[#355E3B]/5 transition-all">
            I&apos;m an Investor
            <span className="text-[#355E3B] transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10 w-full max-w-4xl" style={{ animation: mounted ? "ss-fade-up 1s 0.75s both" : "none", marginTop: "clamp(2rem, 4vh, 4rem)" }}>
          {[{ v: "₹100 Cr", l: "Target Fund" }, { v: "10+", l: "Companies Backed" }, { v: "50+", l: "Operator Mentors" }, { v: "4x", l: "Avg. MOIC" }].map((s) => (
            <div key={s.l} className="text-center sm:text-left border-l-0 sm:border-l border-[#355E3B]/15 sm:pl-5 group">
              <div className="font-serif-display font-semibold text-[#355E3B] transition-transform duration-300 group-hover:-translate-y-1" style={{ fontSize: "clamp(1.6rem, 2.6vw, 2.4rem)" }}>{s.v}</div>
              <div className="mt-1 uppercase tracking-[0.18em] font-sans-jakarta text-[#355E3B]/60" style={{ fontSize: "clamp(0.6rem, 0.7vw, 0.72rem)" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 z-10 border-t border-[#355E3B]/10 bg-white/70 backdrop-blur-md overflow-hidden">
        <div className="flex gap-12 py-3 whitespace-nowrap" style={{ animation: "ss-marquee 38s linear infinite" }}>
          {[...tickerItems, ...tickerItems, ...tickerItems].map((t, i) => (
            <span key={i} className="flex items-center gap-3 text-[12px] uppercase tracking-[0.2em] font-medium text-[#355E3B]/75">
              <span className="w-1.5 h-1.5 bg-[#f4b942] rounded-full" />
              {t}
            </span>
          ))}
        </div>
      </div>

      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2 text-[#355E3B]/60">
        <span className="text-[10px] uppercase tracking-[0.4em]">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#355E3B]/40 to-transparent" />
      </div>
    </div>
  );
}
